import { RotateCcw, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { analyzeAudioSample, metadataToVoiceProfile } from "../audio/analyzeSample";
import { defaultVoiceProfile, nowIso } from "../constants";
import { getJapaneseVoices, speakText } from "../audio/speech";
import type { AppSettings, MediaRecord, VoiceProfile } from "../types/app";
import { validateMediaFile } from "../utils/fileValidation";
import { createId } from "../utils/id";
import { clampVoiceProfile } from "../audio/voiceSettings";

interface Props {
  settings: AppSettings;
  profiles: VoiceProfile[];
  media: MediaRecord[];
  active?: VoiceProfile;
  onSettings: (patch: Partial<AppSettings>) => Promise<void>;
  onSaveProfile: (profile: VoiceProfile) => Promise<void>;
  onSaveMedia: (media: MediaRecord) => Promise<void>;
  onError: (message: string) => void;
}

export function VoicePage({ settings, profiles, media, active, onSettings, onSaveProfile, onSaveMedia, onError }: Props) {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [draft, setDraft] = useState(active ?? defaultVoiceProfile());
  const [rightsOk, setRightsOk] = useState(false);
  const [sourceName, setSourceName] = useState("");
  const [sourceLicense, setSourceLicense] = useState("");
  const [sourceNote, setSourceNote] = useState("");

  useEffect(() => {
    const load = () => setVoices(getJapaneseVoices());
    load();
    window.speechSynthesis?.addEventListener("voiceschanged", load);
    return () => window.speechSynthesis?.removeEventListener("voiceschanged", load);
  }, []);

  useEffect(() => {
    if (active) setDraft(active);
  }, [active]);

  return (
    <main className="screen">
      <h1>音声設定</h1>
      <section className="panel">
        <label>iPhone標準音声
          <select value={settings.selectedVoice ?? ""} onChange={(event) => void onSettings({ selectedVoice: event.target.value })}>
            <option value="">自動選択</option>
            {voices.map((voice) => <option value={voice.voiceURI} key={voice.voiceURI}>{voice.name} / {voice.lang}</option>)}
          </select>
        </label>
        {!voices.length ? <p>音声合成非対応、または日本語音声がまだ読み込まれていません。テキスト表示だけでも会話できます。</p> : null}
        <label>プロファイル
          <select value={draft.id} onChange={(event) => {
            const next = profiles.find((item) => item.id === event.target.value);
            if (next) {
              setDraft(next);
              void onSettings({ selectedVoiceProfileId: next.id });
            }
          }}>
            {profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
          </select>
        </label>
        {(["pitch", "rate", "gain", "warmth", "brightness", "softness", "compression", "reverb", "breathiness"] as const).map((key) => (
          <label key={key}>{key}
            <input type="range" min={key === "pitch" || key === "rate" ? 0.5 : 0} max={key === "gain" ? 1.5 : key === "pitch" ? 1.8 : key === "rate" ? 1.4 : 1} step="0.01" value={draft[key]} onChange={(event) => setDraft(clampVoiceProfile({ ...draft, [key]: Number(event.target.value), updatedAt: nowIso() }))} />
          </label>
        ))}
        <div className="button-row">
          <button type="button" onClick={() => void onSaveProfile(draft)}>保存</button>
          <button type="button" onClick={() => void speakText("こんにちは。今日も少しゆっくり話すね。", settings.selectedVoice, draft)}>テスト再生</button>
          <button type="button" onClick={() => setDraft(defaultVoiceProfile())}><RotateCcw /> 初期値へ戻す</button>
        </div>
      </section>
      <section className="panel">
        <h2>音声サンプル登録</h2>
        <label>素材名<input value={sourceName} onChange={(event) => setSourceName(event.target.value)} /></label>
        <label>出典<input value={sourceNote} onChange={(event) => setSourceNote(event.target.value)} /></label>
        <label>ライセンス<input value={sourceLicense} onChange={(event) => setSourceLicense(event.target.value)} /></label>
        <label className="checkbox"><input type="checkbox" checked={rightsOk} onChange={(event) => setRightsOk(event.target.checked)} /> この音声は、私が所有している音声、CC0音声、またはAI音声への派生利用を明示的に許可された音声です。</label>
        <label className="file-button"><Upload /> WAV、MP3、M4Aを登録
          <input hidden type="file" accept="audio/wav,audio/x-wav,audio/mpeg,audio/mp4,audio/m4a" onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            if (!rightsOk) {
              onError("権利確認チェックがないため保存できません。");
              return;
            }
            const valid = await validateMediaFile(file, "audioSample");
            if (!valid.ok) {
              onError(valid.reason);
              return;
            }
            const metadata = await analyzeAudioSample(file);
            const mediaId = createId("media");
            await onSaveMedia({ id: mediaId, type: "audioSample", filename: file.name, mimeType: file.type, blob: file, metadata: { ...metadata, sourceNote }, createdAt: nowIso() });
            const profile = metadataToVoiceProfile(sourceName || file.name, sourceNote, sourceLicense, metadata);
            await onSaveProfile(profile);
            await onSettings({ selectedVoiceProfileId: profile.id });
          }} />
        </label>
        {media.filter((item) => item.type === "audioSample").map((item) => <p key={item.id}>{item.filename} / {item.metadata?.duration.toFixed(1) ?? "?"}秒</p>)}
      </section>
    </main>
  );
}
